import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { EventTrackLabel } from '../../constants/ga4';
import { useComboProgressContext } from '../../hooks/contexts/comboProgress.context';
import { DataCarouselProps, useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useUiContext } from '../../hooks/contexts/ui.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import GoogleAnalytics from '../../trackings/GoogleAnalytics';
import { TrackingItem } from '../../types/ga4';
import { Ticket } from '../../types/ticket';
import Acordeon from '../EventDetails/Acordeon';
import Select from '../Select';
import * as Styles from './ticket-list.styles';

type SelectType = {
  name: string;
  value: number;
};

const TicketList: FC = () => {
  const [itemCarousel, setItemCarousel] = useState<DataCarouselProps | null>();
  const [ticketsFilteredBySession, setTicketsFilteredBySession] = useState<Ticket[]>([]);
  const [sessions, setSessions] = useState<SelectType[]>([]);
  const {
    tickets,
    selectedIdDateCarousel,
    setSelectedIdDateCarousel,
    isFootball,
    ticketGroupSelected,
    dataCarousel,
    couponApplied,
    passKeyApplied,
    event,
    comboList,
  } = useEventContext();
  const renderASingleTicketList = ticketGroupSelected !== null;
  const { breadcrumbs : comboBreadcrumbs } = useComboProgressContext();
  const { isMobile } = useUiContext();
  const {  handleFormatedDate } = useFormattedDateTimeZone();
  const { currentLocale : { locale } } = useI18nContext();

  const handleTracking = (curItem: DataCarouselProps) => {
    const trackingTickets = tickets.filter(({ id }) => isFootball || curItem?.groupsReference.includes(id));

    let trackingData = [] as TrackingItem[];
    trackingTickets.forEach((ticketGroup) => {
      trackingData = ticketGroup.type.map((curTicket) => ({
        currency : String(event?.accepted_currencies),
        item_id : String(event?.id),
        item_name : String(event?.title),
        item_category : ticketGroup.isPassport ? 'Passaporte' : (
          handleFormatedDate(curTicket.dates[0].datetime, locale, event?.timezone ?? 'America/Sao_Paulo')
        ),
        item_brand : ticketGroup.name,
        item_variant : curTicket.name,
        coupon : couponApplied ? couponApplied.coupon.name : '',
        discount : couponApplied ? couponApplied.coupon.value_discount : 0,
        price : curTicket.price - (couponApplied ? couponApplied.coupon.value_discount : 0),
      }));
    });
    GoogleAnalytics.trackViewItem(EventTrackLabel.VIEW_ITEM, trackingData);
  };

  const handleSessions = () => {
    const sessions = dataCarousel
      .map((item) => ({ name : item.sessionTitle || item.title, value : item.id }));
    setSessions(sessions);
  };

  const onChangeSession = (value: string | number) => {
    const selectedSession = dataCarousel.find(({ id }) => id === Number(value));
    setItemCarousel(selectedSession);
    setSelectedIdDateCarousel(Number(value));
  };

  const filteredTicketGroupSelected = useMemo(() => {
    if (!ticketGroupSelected) return null;

    const eventComboExclusiveSales = event?.attributes.comboExclusiveSales?.value ?? false;
    const hasCombo = comboList.length > 0;

    if (eventComboExclusiveSales || !hasCombo) return ticketGroupSelected;

    return {
      ...ticketGroupSelected,
      type : ticketGroupSelected.type.filter(ticketType => ticketType.comboExclusiveSales !== true),
    };
  }, [ticketGroupSelected, event, comboList]);

  useEffect(() => {
    handleSessions();
    const itemCarousel = dataCarousel.find((item) => item.id === selectedIdDateCarousel)!;
    setItemCarousel(itemCarousel);
    if (itemCarousel) {
      handleTracking(itemCarousel);
    }
  }, [dataCarousel, selectedIdDateCarousel]);

  useEffect(() => {
    if (itemCarousel) {
      const filteredTickets = tickets.filter(ticket => itemCarousel.groupsReference.includes(ticket.id));

      const eventComboExclusiveSales = event?.attributes.comboExclusiveSales?.value ?? false;
      const hasCombo = comboList.length > 0;

      if (!eventComboExclusiveSales && hasCombo) {
        const ticketsWithFilteredTypes = filteredTickets.map(ticket => ({
          ...ticket,
          type : ticket.type.filter(ticketType => ticketType.comboExclusiveSales !== true),
        })).filter(ticket => ticket.type.length > 0);

        setTicketsFilteredBySession(ticketsWithFilteredTypes);
        return;
      }

      setTicketsFilteredBySession(filteredTickets);
    }
  }, [tickets, itemCarousel, event, comboList]);

  if (!itemCarousel) return null;

  const handleOpenedAcordeon = (ticket: Ticket,index: number) => {
    if (couponApplied?.isValid || passKeyApplied)
      return ticket.type.some((type) => type.tagType) || ticket.type.some((type) => type.hidden);

    return ticketsFilteredBySession.length === 1 || index === 0;
  };

  const hasCombo = comboList.length > 0;
  const renderCombo = hasCombo ? (
    itemCarousel.definitiveSessionId === comboList.map((combo) => combo.id).join('')
  ) : false;

  const curComboComponent = comboBreadcrumbs.find((breadcrumb) => breadcrumb.active)?.component;

  const hasCreditCardBanner = Boolean(event?.attributes.exclusiveCreditCardBrand?.value);

  return (
    <Fragment>
      { renderASingleTicketList ? (
        <Styles.Wrapper
          $hasCreditCardBanner={hasCreditCardBanner}
          id="ticket-list-groups"
        >
          <Acordeon
            idRef={filteredTicketGroupSelected.id}
            ticket={filteredTicketGroupSelected}
            opened={true}
          />
        </Styles.Wrapper>
      ) : (
        <Styles.Wrapper
          $hasCreditCardBanner={hasCreditCardBanner}
          id="ticket-list-groups">
          { renderCombo && curComboComponent ? (
            <Fragment>
              {curComboComponent()}
            </Fragment>
          ) : (
            <Fragment>
              {isMobile && sessions.length > 1 && (
                <Select
                  value={selectedIdDateCarousel}
                  onChange={(value: string | number) => onChangeSession(value)}
                  data={sessions}
                />
              )}
              {ticketsFilteredBySession.map((ticket, index) =>
                <Acordeon
                  key={ticket.id}
                  idRef={ticket.id}
                  ticket={ticket}
                  opened={handleOpenedAcordeon(ticket, index)}
                />
              )}
            </Fragment>
          )}
        </Styles.Wrapper>
      )}
    </Fragment>
  );
};

export default TicketList;
